'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useSubscriptionQuery } from '../api/billingApi';

/**
 * Landing page Stripe redirects to after a successful checkout
 * (`success_url` in `useCheckout.ts`).
 *
 * The actual activation happens on the server when Stripe POSTs a
 * `checkout.session.completed` webhook to `/webhooks/stripe`. That can
 * arrive milliseconds after the user is redirected here. We poll the
 * subscription endpoint a few times before giving up; almost always
 * the second poll already shows the new tier.
 */
export function BillingSuccessPage() {
  const router = useRouter();
  const params = useSearchParams();
  const sessionId = params?.get('session_id') ?? null;

  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 6; // ~6 × 1.5s = 9s budget for webhook arrival

  const { data: sub, refetch } = useSubscriptionQuery();

  const activated = sub?.status === 'active' && sub.tier !== 'free';
  const stillWaiting = !activated && attempts < MAX_ATTEMPTS;

  useEffect(() => {
    if (!stillWaiting) return;
    const t = setTimeout(() => {
      void refetch();
      setAttempts((a) => a + 1);
    }, 1500);
    return () => clearTimeout(t);
  }, [stillWaiting, refetch]);

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 56px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: 'var(--pm-paper)',
          border: '1px solid var(--pm-rule)',
          borderRadius: 16,
          padding: '40px 36px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 999,
            background: activated ? 'var(--pm-emerald-soft)' : 'var(--pm-cream-2)',
            margin: '0 auto 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {activated ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <polyline
                points="5 12 10 17 19 7"
                stroke="var(--pm-emerald)"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <div
              style={{
                width: 22,
                height: 22,
                border: '2.5px solid var(--pm-ink-3)',
                borderTopColor: 'transparent',
                borderRadius: 999,
                animation: 'pmSpin 0.9s linear infinite',
              }}
            />
          )}
        </div>

        <h1
          className="ad-serif"
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 500,
            letterSpacing: -0.4,
            color: 'var(--pm-ink)',
          }}
        >
          {activated ? 'You’re in. Welcome to PropMind.' : 'Confirming your payment…'}
        </h1>

        <p
          style={{
            fontSize: 14,
            color: 'var(--pm-ink-3)',
            marginTop: 12,
            lineHeight: 1.55,
          }}
        >
          {activated ? (
            <>
              Your <strong>{sub?.tier}</strong> subscription is active until{' '}
              {sub?.current_period_end
                ? new Date(sub.current_period_end).toLocaleDateString()
                : '—'}
              . An invoice receipt is on its way to your email.
            </>
          ) : stillWaiting ? (
            <>
              Stripe confirmed your card. We’re waiting for the webhook to
              activate your subscription — this usually takes a second or two.
            </>
          ) : (
            <>
              We received your payment but haven’t seen the activation event
              yet. Refresh in a minute or two — if the issue persists,
              contact support with this reference:{' '}
              <span className="ad-mono" style={{ color: 'var(--pm-ink-2)' }}>
                {sessionId ?? '—'}
              </span>
              .
            </>
          )}
        </p>

        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'center',
            marginTop: 28,
          }}
        >
          <button
            type="button"
            onClick={() => router.push('/billing')}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              background: 'var(--pm-ink-fixed)',
              color: '#fff',
              border: '1px solid var(--pm-ink-fixed)',
              fontSize: 13.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            View billing
          </button>
          {activated && (
            <button
              type="button"
              onClick={() => router.push('/monitoring')}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                background: 'var(--pm-paper)',
                color: 'var(--pm-ink)',
                border: '1px solid var(--pm-rule)',
                fontSize: 13.5,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Go to monitor →
            </button>
          )}
        </div>

        <style>{`@keyframes pmSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
